import api from '../../../services/api';
import { toAnalyticsParams } from '../../../components/performance/pmAnalyticsFilters';
import { formatDate, formatDateTime } from '../../../utils/Utils';

// PM analitik P7b — E16 Rapor merkezi veri katmanı. Uçlar:
//   GET  v1/performance-report-center/templates                      (rol bazlı katalog)
//   GET  v1/performance-report-center/templates/{key}/preview?…      (ortak süzgeç + ek süzgeç + sayfa)
//   POST v1/performance-report-center/templates/{key}/export         ({ format, query, labels } → dosya; her indirme denetlenir)
//   GET  v1/performance-report-center/runs | runs/summary | runs/mine (denetim: yalnız İK; "son çalıştırdıklarım": herkes)
// api.js GET'te reject ETMEZ — hata sinyali res.data.status ("yüklenemedi" ile "boş" ayrı durumlardır).

export const BASE = '/v1/performance-report-center';

export const FAMILIES = [
  'process', 'results', 'goals', 'competency', 'reviews', 'calibration', 'talent', 'development', 'meetings', 'organization',
  'trend', 'experience', 'ai', 'audit',
];

export class ReportError extends Error {}

const payload = (res) => (res?.data?.status ? res.data.data : undefined);

async function get(url, params) {
  const res = await api.get(url, { params });
  const data = payload(res);
  if (data === undefined) throw new ReportError(res?.data?.message || 'error');
  return data;
}

export const fetchTemplates = () => get(`${BASE}/templates`);
export const fetchPreview = (key, params) => get(`${BASE}/templates/${key}/preview`, params);
export const fetchMyRuns = () => get(`${BASE}/runs/mine`, { pageSize: 5 });
export const fetchRuns = (params) => get(`${BASE}/runs`, params);
export const fetchRunSummary = (params) => get(`${BASE}/runs/summary`, params);

// ---------------------------------------------------------------- şablona özgü ek süzgeçler (GenericSelect seçenekleri)
export const EXTRA_OPTIONS = {
  'stage[eq]': ['goal_setting', 'goal_evaluation', 'self_review', 'manager_review', 'nomination', 'feedback_360', 'calibration', 'final_result', 'result_ack'],
  'status[in]': ['not_opened', 'open', 'draft', 'submitted', 'returned', 'done', 'exempt', 'cancelled'],
  overdue: ['true', 'false'],
  'assigneeRole[in]': ['employee', 'manager', 'hr', 'rater'],
  mandatory: ['true', 'false'],
  dimension: ['department', 'manager', 'title', 'location', 'grade', 'jobFamily'],
  periods: ['2', '3', '4', '5', '6', '7', '8'],
  'meetingStatus[eq]': ['not_planned', 'planned', 'held', 'cancelled'],
  'confirmation[eq]': ['confirmed', 'not_confirmed', 'outdated'],
  'meetingDue[eq]': ['overdue', 'week'],
  'status[eq]': ['open', 'accepted', 'rejected'],
  'template[eq]': [],
  'format[eq]': ['xlsx', 'pdf'],
  'runStatus[eq]': ['succeeded', 'failed', 'too_large', 'timeout'],
};

// Deneyim anketi şablonunda boyut yalnız departman / ünvan / yönetici (anonim cevapta lokasyon yok).
export const SURVEY_DIMENSIONS = ['department', 'title', 'manager'];

// Tarih aralığı süzgeçleri (audit-changes, report-runs) DateTimePicker ile verilir; diğerleri seçim kutusu.
export const DATE_FILTERS = ['createDate[between]', 'createDate[gte]', 'createDate[lte]'];

/** Ek süzgeç değerinin etiket anahtarı (ör. stage[eq] → aşama çevirisi). */
export function extraValueKey(name, value) {
  switch (name) {
    case 'stage[eq]': return `performance.insight.stage.${value}`;
    case 'status[in]': return `performance.operations.taskStatus.${value}`;
    case 'assigneeRole[in]': return `performance.reminder.role.${value}`;
    case 'status[eq]': return `performance.objections.status.${value}`;
    case 'meetingStatus[eq]': return `performance.reportCenter.values.meetingStatus.${value}`;
    case 'confirmation[eq]': return `performance.reportCenter.values.confirmationStatus.${value}`;
    case 'runStatus[eq]': return `performance.reportCenter.values.runStatus.${value}`;
    case 'format[eq]': return `performance.reportCenter.values.format.${value}`;
    case 'template[eq]': return `performance.reportCenter.templates.${value}.title`;
    default: return `performance.reportCenter.extraValue.${name.replace(/\[.*\]$/, '')}.${value}`;
  }
}

/** Ek süzgeç seçenekleri (etiketli). "periods" sayıdır, çevrilmez; gelişim planı şablonunda durum = plan durumu. */
// Kademe / iş ailesi kırılımı yalnız İK (sunucuda 403 — spec 2026-10-27 §7.6).
export const HR_ONLY_DIMENSIONS = ['grade', 'jobFamily'];

export function extraOptions(t, name, templates, templateKey, isHr = false) {
  if (name === 'status[eq]' && templateKey === 'idp-completion') {
    return ENUM_VALUES.idpStatus.map((v) => ({ id: v, name: enumLabel(t, 'idpStatus', v) }));
  }
  let values = name === 'template[eq]' ? (templates || []).map((tpl) => tpl.key) : (EXTRA_OPTIONS[name] || []);
  if (name === 'dimension' && !isHr) values = values.filter((v) => !HR_ONLY_DIMENSIONS.includes(v));
  return values.map((v) => ({ id: v, name: name === 'periods' ? v : t(extraValueKey(name, v), { defaultValue: v }) }));
}

// Arayüzde gösterilmeyen ek süzgeçler (sunucu destekler; varsayılan yeterli): 9 Kutu süreci verilmezse son tamamlanan süreç.
export const HIDDEN_FILTERS = ['processId[eq]'];

// ---------------------------------------------------------------- sorgu
/**
 * Ön izleme / dosya için sorgu parametreleri. Dönemli şablon ortak F0 sözleşmesini (toAnalyticsParams) taşır; dönemsiz şablona
 * dönem gönderilmez (sunucu bilinmeyen parametreyi 400 ile reddeder). Puan hali yalnız destekleyen şablonda ve final değilse yazılır.
 */
export function buildQuery(template, filters, extra = {}, scoreState = 'final') {
  const params = template?.requiresPeriod ? { ...toAnalyticsParams(filters) } : {};
  if (template?.requiresPeriod && template.supportsScoreState && scoreState && scoreState !== 'final') params.scoreState = scoreState;
  Object.entries(extra || {}).forEach(([k, v]) => {
    if (v != null && v !== '' && (template?.extraFilters || []).includes(k)) params[k] = String(v);
  });
  return params;
}

// ---------------------------------------------------------------- enum etiketleri
// Var olan çeviri ad alanları yeniden kullanılır; olmayanlar performance.reportCenter.values.{enumKey}.{değer}.
const ENUM_KEY = {
  stage: (v) => `performance.insight.stage.${v}`,
  task: (v) => `performance.operations.taskKey.${v}`,
  taskStatus: (v) => `performance.operations.taskStatus.${v}`,
  assigneeRole: (v) => `performance.reminder.role.${v}`,
  exemptReason: (v) => `performance.period.participants.reasons.${v}`,
  periodStatus: (v) => `performance.period.status.${v}`,
  goalType: (v) => `performance.goal.goalType.${v}`,
  goalStatus: (v) => `performance.goal.status.${v}`,
  kpiUnit: (v) => `performance.goal.kpiUnit.${v}`,
  kpiDirection: (v) => `performance.goal.kpiDirection.${v}`,
  evaluatorRole: (v) => `performance.feedback360.role.${v}`,
  band: (v) => `performance.review.band.${v}`,
  pipStatus: (v) => `performance.pip.status.${v}`,
  criticalPositionStatus: (v) => `performance.succession.status.${v}`,
  idpStatus: (v) => `performance.idp.status.${v}`,
  nineBoxCell: (v) => `performance.ninebox.cell.${v}`,
  insightRule: (v) => `performance.insight.${v}.title`,
  insightSeverity: (v) => `performance.insight.severityValue.${v}`,
  insightScope: (v) => `performance.insight.scope.${v}`,
  insightStatus: (v) => `performance.insight.statusValue.${v}`,
  reportTemplate: (v) => `performance.reportCenter.templates.${v}.title`,
};

// Dosyada bütün olası değerlerin etiketi gerekir (önizleme sayfasında görünmeyenler de dökülür).
export const ENUM_VALUES = {
  stage: EXTRA_OPTIONS['stage[eq]'],
  task: ['goal_submit', 'goal_employee_closure', 'self_review', 'nomination_propose', 'calibration_employee', 'result_acknowledge',
    'goal_manager_approval', 'goal_manager_scoring', 'manager_review', 'nomination_approve', 'calibration_manager', 'goal_hr_approval', 'rater_response'],
  taskStatus: EXTRA_OPTIONS['status[in]'],
  assigneeRole: EXTRA_OPTIONS['assigneeRole[in]'],
  exemptReason: ['long_leave', 'parental_leave', 'role_change', 'other'],
  periodStatus: ['draft', 'active', 'evaluation', 'calibration', 'pending_close', 'closed', 'cancelled'],
  slaStep: ['pm.manager_approval', 'pm.hr_approval', 'pm.revision_manager_approval', 'pm.revision_hr_approval'],
  evaluatorRole: ['self', 'manager', 'peer', 'subordinate', 'senior'],
  resultState: ['none', 'preliminary', 'calibrated', 'published'],
  band: ['high', 'mid', 'low'],
  goalType: ['okr', 'kpi', 'project'],
  goalStatus: ['draft', 'manager_approval', 'hr_approval', 'approved', 'revision_required', 'evaluating', 'closed', 'abandoned'],
  kpiUnit: ['numeric', 'currency', 'percent'],
  kpiDirection: ['increase', 'decrease'],
  calibrationChange: ['session_move', 'objection_revision'],
  nineBoxCell: ['attention_needed', 'reliable_performer', 'expert_master', 'developing', 'core_employee', 'high_performer', 'hidden_talent', 'promising', 'star_hipo'],
  criticalPositionStatus: ['draft', 'active', 'review_needed', 'filled', 'closed'],
  idpStatus: ['draft', 'pending_approval', 'active', 'revision_requested', 'completed', 'cancelled', 'meeting', 'manager_approval', 'revision_required', 'period_end_review'],
  pipStatus: ['draft', 'active', 'completed_successful', 'completed_unsuccessful', 'cancelled'],
  meetingStatus: EXTRA_OPTIONS['meetingStatus[eq]'],
  confirmationStatus: EXTRA_OPTIONS['confirmation[eq]'],
  objectionStatus: ['open', 'accepted', 'rejected', 'none'],
  periodOrigin: ['native', 'imported'],
  trendMethod: ['scale', 'goals'],
  experienceQuestion: ['clarity', 'fairness', 'usefulness'],
  insightSeverity: ['red', 'amber'],
  insightScope: ['tenant', 'department', 'manager', 'employee', 'stage', 'rule'],
  insightStatus: ['new', 'reviewing', 'actioned', 'closed', 'invalid', 'expired'],
  actorScope: ['hr', 'manager'],
  format: ['xlsx', 'pdf'],
  runStatus: EXTRA_OPTIONS['runStatus[eq]'],
};

export function enumLabel(t, enumKey, value) {
  if (value == null || value === '') return '';
  const own = `performance.reportCenter.values.${enumKey}.${value}`;
  const primary = ENUM_KEY[enumKey] ? ENUM_KEY[enumKey](value) : own;
  return t(primary, { defaultValue: t(own, { defaultValue: String(value) }) });
}

// ---------------------------------------------------------------- hücre biçimi (ekran)
export const formatNumber = (v, lang, digits = 2) => (v == null || v === '' ? '' : new Intl.NumberFormat(lang, { maximumFractionDigits: digits }).format(Number(v)));

export function cellText(t, lang, column, value) {
  if (value == null || value === '') return '-';
  switch (column.type) {
    case 'number': return formatNumber(value, lang, 2);
    case 'score': return formatNumber(value, lang, 2);
    case 'percent': return `${formatNumber(value, lang, 2)}%`;
    case 'date': return formatDate(value, lang);
    case 'datetime': return formatDateTime(value, lang);
    case 'bool': return value ? t('performance.report.col.yes') : t('performance.report.col.no');
    case 'enum': return enumLabel(t, column.enumKey, value);
    default: return String(value);
  }
}

export const columnLabel = (t, key) => t(`performance.reportCenter.columns.${key}`, { defaultValue: key });

/** Sunucunun sütun tanımından DataTable sütunları (sıralama/süzme yok — süzgeç şerit ve ek süzgeçlerle). */
export function buildColumns(t, lang, columns) {
  return (columns || []).map((c) => ({
    name: c.key,
    label: columnLabel(t, c.key),
    options: { filter: false, sort: false, customBodyRender: (v) => cellText(t, lang, c, v) },
  }));
}

export const metricLabel = (t, code) => t(`performance.reportCenter.metric.${code}`, { defaultValue: code });

const HEADER_KEYS = ['period', 'scope', 'orgView', 'scoreState', 'scale', 'dataSnapshotAt', 'preparedAt', 'threshold', 'suppressedCells',
  'totalRows', 'truncated', 'suppressed', 'uncalculatedMetrics'];

/** Dosya etiketleri: ekranın dilinde başlık, sütun, başlık satırı, enum değerleri, metrik ve not adları (sunucu çeviri tutmaz). */
export function buildLabels(t, template, preview) {
  const columns = {};
  const values = {};
  (preview?.columns || []).forEach((c) => {
    columns[c.key] = columnLabel(t, c.key);
    if (c.type === 'enum' && c.enumKey) {
      const seen = new Set(ENUM_VALUES[c.enumKey] || []);
      (preview?.rows || []).forEach((r) => { if (r[c.key] != null) seen.add(String(r[c.key])); });
      values[c.enumKey] = {};
      seen.forEach((v) => { values[c.enumKey][v] = enumLabel(t, c.enumKey, v); });
    }
  });
  values._scope = Object.fromEntries(['tenant', 'subtree', 'team'].map((v) => [v, t(`performance.reportCenter.scope.${v}`)]));
  values._orgView = Object.fromEntries(['snapshot', 'current'].map((v) => [v, t(`performance.operations.orgView.${v}`)]));
  values._scoreState = Object.fromEntries(['final', 'preliminary'].map((v) => [v, t(`performance.reportCenter.scoreState.${v}`)]));
  values._periodStatus = Object.fromEntries(ENUM_VALUES.periodStatus.map((v) => [v, t(`performance.period.status.${v}`)]));
  values._bool = { true: t('performance.report.col.yes'), false: t('performance.report.col.no') };
  const header = Object.fromEntries(HEADER_KEYS.map((k) => [k, t(`performance.reportCenter.header.${k}`)]));
  const metrics = {};
  [...(template?.analysisMetrics || []), ...(preview?.metrics || []).map((m) => m.metricCode)].forEach((code) => {
    metrics[code] = metricLabel(t, code);
  });
  const notes = {};
  (preview?.notes || []).forEach((n) => { notes[n] = t(n, { defaultValue: n }); });
  return {
    title: t(`performance.reportCenter.templates.${template?.key}.title`, { defaultValue: template?.key }),
    columns, header, values, metrics, notes,
  };
}

/**
 * Dosya indirme (POST; blob). Başarısız istekte interceptor uyarıyı zaten gösterir — burada yalnız false döner (bileşende toast yok).
 */
export async function downloadReport(key, format, query, labels) {
  try {
    const res = await api.post(`${BASE}/templates/${key}/export`, { format, query, labels }, { responseType: 'blob' });
    if (!res?.headers || !res.data) return false;
    const blob = new Blob([res.data], { type: res.headers['content-type'] });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const match = res.headers['content-disposition']?.match(/filename[^;=\n]*=([^;\n]*)/);
    a.download = match ? match[1].replaceAll('"', '') : `${key}.${format}`;
    // Bağlantı DOM'a eklenip kaldırılır; eşzamanlı revoke bazı Safari/Firefox sürümlerinde indirmeyi iptal eder.
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
    return true;
  } catch {
    return false;
  }
}

/** Katalog seçenekleri: aile sırasıyla, "Aile · Rapor" etiketiyle (GenericSelect araması ikisinde de çalışır). */
export function templateOptions(t, templates, family) {
  return [...(templates || [])]
    .filter((tpl) => !family || tpl.family === family)
    .sort((a, b) => FAMILIES.indexOf(a.family) - FAMILIES.indexOf(b.family))
    .map((tpl) => ({
      id: tpl.key,
      name: `${t(`performance.reportCenter.family.${tpl.family}`)} · ${t(`performance.reportCenter.templates.${tpl.key}.title`, { defaultValue: tpl.key })}`,
    }));
}
